<template>
  <section class="legacy-hero">
    <div class="legacy-hero__inner">
      <div class="legacy-hero__head">
        <div class="legacy-hero__eyebrow">
          {{ eyebrow }}
        </div>
        <div class="legacy-hero__title-row">
          <h1 class="legacy-hero__title">
            {{ title }}
          </h1>
          <span
            v-if="isActive"
            class="legacy-hero__badge"
          >
            {{ t('billing.subscriptionPage.legacyHero.badgeActive') }}
          </span>
        </div>
        <p
          v-if="subtitle"
          class="legacy-hero__subtitle"
        >
          {{ subtitle }}
        </p>
      </div>

      <div
        v-if="isActive"
        class="legacy-hero__meta"
      >
        <div class="legacy-hero__modules">
          <span class="legacy-hero__modules-title">
            {{ t('billing.subscriptionPage.legacyHero.modulesTitle') }}
          </span>
          <div
            v-for="module in modules"
            :key="module.key"
            class="legacy-hero__module"
            :class="{ 'legacy-hero__module--off': !module.active }"
          >
            <i
              class="legacy-hero__module-icon"
              :class="module.active
                ? 'fa-solid fa-circle-check'
                : 'fa-regular fa-circle'"
            />
            <span class="legacy-hero__module-text">
              <span class="legacy-hero__module-name">{{ module.name }}</span>
              <span class="legacy-hero__module-detail">{{ module.detail }}</span>
            </span>
          </div>
        </div>

        <div
          v-if="formattedRenewal"
          class="legacy-hero__row"
        >
          <i class="fa-regular fa-calendar legacy-hero__row-icon" />
          <span>
            {{ t('billing.subscriptionPage.legacyHero.nextRenewal') }}
            <strong>{{ formattedRenewal }}</strong>
            <span class="legacy-hero__row-hint">· {{ autoRenewLabel }}</span>
          </span>
        </div>

        <div
          v-if="metricStat"
          class="legacy-hero__metric"
        >
          <span class="legacy-hero__metric-icon">
            <i class="fa-regular fa-chart-line" />
          </span>
          <span class="legacy-hero__metric-text">
            <span class="legacy-hero__metric-value">{{ metricStat.value }}</span>
            <span class="legacy-hero__metric-label">{{ metricStat.label }}</span>
          </span>
        </div>
      </div>
    </div>

    <div class="legacy-hero__actions">
      <SpmButton
        :label="isActive
          ? t('billing.subscriptionPage.legacyHero.actionManage')
          : t('billing.subscriptionPage.legacyHero.actionChoose')"
        icon="fa-regular fa-arrow-right"
        icon-pos="right"
        class-style="legacy-hero__primary p-button"
        @click="$emit('change-plan')"
      />
    </div>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import moment from 'moment';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { LegacyPlanSection } from '@/types/billing-types';

export default defineComponent({
  name: 'LegacyPlanHeroCard',

  components: { SpmButton },

  props: {
    legacyPlan: {
      type: Object as PropType<LegacyPlanSection>,
      required: true,
    },
  },

  emits: ['change-plan'],

  setup(props) {
    const { t } = useI18n();

    const isActive = computed(() => props.legacyPlan.status === 'active');

    const eyebrow = computed(() => (isActive.value
      ? t('billing.subscriptionPage.legacyHero.eyebrowCurrent')
      : t('billing.subscriptionPage.legacyHero.eyebrowNone')));

    const title = computed(() => (
      props.legacyPlan.bundleName
      ?? t('billing.subscriptionPage.legacyHero.noOfferTitle')
    ));

    const subtitle = computed(() => (isActive.value
      ? t('billing.subscriptionPage.legacyHero.subtitleActive')
      : t('billing.subscriptionPage.legacyHero.subtitleNone')));

    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);

    // Automation + Newsletter modules, each with its monthly allowance as
    // sub-text. Both are always listed so the shop sees what it could add.
    const modules = computed(() => {
      const { hasAutomation, hasNewsletter, pageViewsCap, newsletterCap } = props.legacyPlan;
      return [
        {
          key: 'automation',
          name: t('billing.subscriptionPage.legacyHero.moduleAutomation'),
          active: hasAutomation,
          detail: hasAutomation && pageViewsCap
            ? t('billing.subscriptionPage.legacyHero.capPageViews', {
              count: formatNumber(pageViewsCap),
            })
            : t('billing.subscriptionPage.legacyHero.moduleNotIncluded'),
        },
        {
          key: 'newsletter',
          name: t('billing.subscriptionPage.legacyHero.moduleNewsletter'),
          active: hasNewsletter,
          detail: hasNewsletter && newsletterCap
            ? t('billing.subscriptionPage.legacyHero.capNewsletter', {
              count: formatNumber(newsletterCap),
            })
            : t('billing.subscriptionPage.legacyHero.moduleNotIncluded'),
        },
      ];
    });

    const formattedRenewal = computed(() => (
      props.legacyPlan.renewalAt
        ? moment(props.legacyPlan.renewalAt).format('DD MMMM YYYY')
        : null
    ));

    const autoRenewLabel = computed(() => (props.legacyPlan.autoRenew
      ? t('billing.subscriptionPage.legacyHero.autoRenewOn')
      : t('billing.subscriptionPage.legacyHero.autoRenewOff')));

    const metricStat = computed(() => {
      const m = props.legacyPlan.metricTotal;
      if (!m) return null;
      return {
        value: formatNumber(m.value),
        label: t('billing.subscriptionPage.legacyHero.metricPagesViewed'),
      };
    });

    return {
      t,
      isActive,
      eyebrow,
      title,
      subtitle,
      modules,
      formattedRenewal,
      autoRenewLabel,
      metricStat,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.legacy-hero {
  position: relative;
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  color: #fff;
  border-radius: 16px;
  padding: 32px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 12px 32px -8px rgba(0, 0, 0, 0.08);

  &::before {
    content: "";
    position: absolute;
    top: -120px;
    right: -120px;
    width: 320px;
    height: 320px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(148, 200, 64, 0.32) 0%, transparent 70%);
    pointer-events: none;
  }

  &__inner {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 32px;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 16px;
    }
  }

  &__head {
    min-width: 0;
  }

  &__eyebrow {
    font-size: 0.75rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 600;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 6px;
  }

  &__title {
    font-size: 2rem;
    font-weight: 700;
    margin: 0;
    line-height: 1.1;
    letter-spacing: -0.02em;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 100px;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    background: rgba(148, 200, 64, 0.18);
    color: #c1e07e;
  }

  &__subtitle {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 8px 0 0;
    line-height: 1.5;
  }

  &__meta {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  // ─── Modules (automation / newsletter) ───────────────────────────
  &__modules {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
  }

  &__modules-title {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(255, 255, 255, 0.55);
  }

  &__module {
    display: flex;
    align-items: center;
    gap: 10px;

    &--off {
      opacity: 0.5;
    }
  }

  &__module-icon {
    width: 18px;
    text-align: center;
    color: #c1e07e;
    font-size: 0.95rem;

    .legacy-hero__module--off & {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  &__module-text {
    display: flex;
    flex-direction: column;
    line-height: 1.3;
    min-width: 0;
  }

  &__module-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: #fff;
  }

  &__module-detail {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.78);

    strong {
      color: #fff;
      font-weight: 600;
    }
  }

  &__row-icon {
    width: 16px;
    color: rgba(255, 255, 255, 0.5);
  }

  &__row-hint {
    color: rgba(255, 255, 255, 0.5);
  }

  // ─── Headline usage metric (pages viewed) ────────────────────────
  &__metric {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 4px;
    padding: 12px 14px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
  }

  &__metric-icon {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(148, 200, 64, 0.16);
    color: #c1e07e;
    font-size: 0.85rem;
  }

  &__metric-text {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
    min-width: 0;
  }

  &__metric-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.01em;
  }

  &__metric-label {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
  }

  &__actions {
    position: relative;
    z-index: 1;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 24px;
  }

  &__primary {
    background: $brand-color-primary !important;
    border-color: $brand-color-primary !important;
    color: #fff !important;
    font-weight: 600 !important;

    &:hover {
      background: #7aae26 !important;
      border-color: #7aae26 !important;
    }
  }
}
</style>
